import { Navigate, useNavigate } from "react-router-dom";
import { Provider } from "react-redux";

import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";
import { datadogRum } from "@datadog/browser-rum";

import { AuthProvider } from "@covertree/auth";
import { fetchAuthSession } from "aws-amplify/auth";
import PageLayout from "@covertree/layouts/PageLayout";
import {
  AUTH_ROUTES,
  BASE_AUTH_PATH,
  createAuthRoutes,
  ProtectedRoute,
} from "@covertree/routing";
import { BannerPortalName } from "@covertree/ui";
import { Bonsai, Sequoia as SequoiaLogo } from "@covertree/ui/brand/Logo";
import { TrackingProvider } from "@covertree/tracking";

import WholesalerProvider from "@providers/WholesalerProvider";
import { RenewalActionsProvider } from "@providers/RenewalActionsProvider";
import { ContinueQuoteProvider } from "@providers/ContinueQuoteProvider";
import { UWReasonProvider } from "@providers/UWReasonProvider";
import BinderDocProvider from "@providers/BinderDocProvider";

import { ALLOWED_OAUTH_PROVIDERS } from "@consts/index";

import { Search } from "@components/Search";

import AccessPatternProvider from "@providers/AccessPatternProvider/AccessPatternProvider";
import { CacheUtilitiesProvider } from "@providers/CacheUtilitiesProvider/CacheUtilitiesProvider";

import { resetCache, store } from "@redux/store";

import {
  BASE_ENDORSEMENT_PATH,
  BASE_NEW_POLICY_PATH,
  EndorsementRoutes,
  OVERVIEW_PATH,
  PENDING_CANCELLATION_PATH,
  POLICIES_PATH,
  QUOTES_PATH,
  RENEWALS_PATH,
  SEQUOIA_PATH,
  BULK_QUOTES_PATH,
  SEQUOIA_UPLOAD_PATH,
  SEQUOIA_STATUSES_PATH,
  SEQUOIA_CONTINUE_PATH,
  QUOTES_CONTINUE_PATH,
  FAST_DECLINE_PATH,
} from "@routing/paths";
import { Overview } from "@routing/routes/Overview";

import { getContactUsSections } from "@utils/getContactUsSections";
import {
  clearFeatureFlagsUserDetails,
  setupFeatureFlagsUserDetails,
  featureFlags,
} from "@utils/featureFlags";
import {
  identifyChameleonUser,
  clearChameleon,
  registerChameleonNavigationListener,
} from "@utils/chameleon/chameleon";

import { NewPolicyLayout } from "@layouts/NewPolicyLayout";
import { EndorsementLayout } from "@layouts/EndorsementLayout";
import { CYQRedZoneErrorModal } from "@components/CYQRedZoneErrorModal";
import { useWebTracker } from "@covertree/web-tracker";

import Policies from "./routes/Policies";
import Renewals from "./routes/Renewals";
import { RenewalDetails } from "./routes/Renewals/RenewalDetails";
import Quotes from "./routes/Quotes";
import PendingCancellationPolicies from "./routes/PendingCancellationPolicies";
import { BulkQuotes } from "./routes/BulkQuotes";

import { PolicyDetails } from "./routes/Policies/PolicyDetails";
import {
  CheckoutStep as EndorsementCheckoutStep,
  CoverageStep as EndorsementCoverageStep,
  InsuredInfoStep as EndorsementInsuredInfoStep,
  SummaryStep as EndorsementSummaryStep,
  UwReviewStep as EndorsementUwReviewStep,
} from "./routes/Policies/PolicyDetails/Endorsements";

import Sequoia from "./routes/Sequoia";
import { SequoiaUpload } from "./routes/SequoiaUpload";
import { SequoiaStatuses } from "./routes/SequoiaUpload/SequoiaStatuses";

import { SequoiaContinueLoading } from "./routes/Sequoia/SequoiaContinueLoading";
import { QuotesContinueLoading } from "./routes/Quotes/QuotesContinueLoading";

// TODO: Should it be within Routes??
import { TopBarMenu } from "./routes/TopBarMenu";
import { newPolicyRoutes } from "./newPolicyRoutes";

export const router = createBrowserRouter([
  {
    path: "*",
    element: (
      <CacheUtilitiesProvider>
        {({ clearCache }) => {
          const webTracker = useWebTracker();
          const navigate = useNavigate();

          return (
            <AuthProvider
              authFlowType="USER_PASSWORD_AUTH"
              createNewPasswordSuccessPath={`${BASE_AUTH_PATH}/${AUTH_ROUTES.CREATE_NEW_PASSWORD_SUCCESS}`}
              createNewPasswordPath={`${BASE_AUTH_PATH}/${AUTH_ROUTES.CREATE_NEW_PASSWORD}`}
              confirmSignInPath={`${BASE_AUTH_PATH}/${AUTH_ROUTES.CONFIRM_SIGN_IN}`}
              onSignIn={(basicUserData) => {
                setupFeatureFlagsUserDetails(basicUserData);
                datadogRum.setUser({
                  id: basicUserData.sub,
                  name: `${basicUserData.firstName} ${basicUserData.lastName}`,
                  email: basicUserData.email,
                  groups: basicUserData.groups,
                });
                webTracker.identify({
                  agentId: basicUserData.agentId,
                  properties: {
                    agentId: basicUserData.agentId,
                    agentEmail: basicUserData.email,
                    agentDateOfBirth: basicUserData.agentDateOfBirth,
                    agencyId: basicUserData.agencyId,
                    agencyName: basicUserData.agencyName,
                    agencyType: basicUserData.agencyType,
                    wholesalerId: basicUserData.wholesalerId,
                  },
                });

                fetchAuthSession()
                  .then((session) => {
                    const chameleonUserHash = session.tokens?.idToken?.payload
                      ?.chameleonUserHash as string | undefined;
                    identifyChameleonUser(basicUserData, chameleonUserHash);
                  })
                  .catch(() => identifyChameleonUser(basicUserData));

                registerChameleonNavigationListener(navigate);
              }}
              onSignOut={() => {
                clearCache();
                resetCache();
                clearFeatureFlagsUserDetails();
                datadogRum.clearUser();
                webTracker.reset({ resetDevice: true });
                clearChameleon();
              }}>
              <TrackingProvider>
                <BinderDocProvider>
                  <WholesalerProvider />
                </BinderDocProvider>
              </TrackingProvider>
            </AuthProvider>
          );
        }}
      </CacheUtilitiesProvider>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={`${BASE_AUTH_PATH}/${AUTH_ROUTES.LOGIN}`} />,
      },
      // Login related routes
      ...createAuthRoutes({
        loginPage: {
          title: "CoverTree Agent Portal",
        },
        ssoProvidersList: ALLOWED_OAUTH_PROVIDERS,
        isPasswordless: false,
        isMFAEnabled: false,
        useManualForgotPassword: true,
        whenLoggedIn: () => `/${OVERVIEW_PATH}`,
        logo: <Bonsai height={100} width={86} />,
      }),
      // Protected routes
      {
        path: "*",
        element: (
          <ProtectedRoute
            shouldBeLoggedIn={true}
            whenNotAllowed={() => BASE_AUTH_PATH}
            whenNotAllowedForSSO={(provider: string) =>
              `${BASE_AUTH_PATH}/${AUTH_ROUTES.SSO_LOGIN}?provider=${provider}`
            }
          />
        ),
        children: [
          {
            path: "*",
            element: (
              <RenewalActionsProvider>
                <ContinueQuoteProvider>
                  <UWReasonProvider>
                    <AccessPatternProvider>
                      {({ isInternalAgent, isSugAgent, pathname }) => (
                        <PageLayout
                          enableBanner={featureFlags.banners.isEnabled()}
                          bannerPortalName={BannerPortalName.AgentPortal}
                          navigationItems={[
                            { name: "Overview", path: `/${OVERVIEW_PATH}` },
                            { name: "Quotes", path: `/${QUOTES_PATH}` },
                            { name: "Policies", path: `/${POLICIES_PATH}` },
                            {
                              name: "Pending cancellation",
                              path: `/${PENDING_CANCELLATION_PATH}`,
                            },
                            { name: "Renewals", path: `/${RENEWALS_PATH}` },
                            { name: "Sequoia", path: `/${SEQUOIA_PATH}` },
                          ]}
                          bottomNavigationItems={[
                            {
                              name: "Agent Guide",
                              href: isSugAgent
                                ? "https://covertree.com/covertree-agent-guide-sug/"
                                : "https://covertree.com/covertree-agent-guide/",
                            },
                          ]}
                          logo={
                            pathname === `/${SEQUOIA_PATH}` ? (
                              <SequoiaLogo height={100} width={86} />
                            ) : (
                              <Bonsai height={100} width={86} />
                            )
                          }
                          contacts={getContactUsSections(isInternalAgent)}
                          menu={<TopBarMenu />}
                          search={
                            <Provider store={store}>
                              <Search />
                            </Provider>
                          }
                        />
                      )}
                    </AccessPatternProvider>
                  </UWReasonProvider>
                </ContinueQuoteProvider>
              </RenewalActionsProvider>
            ),
            children: [
              {
                path: OVERVIEW_PATH,
                element: <Overview />,
                children: [
                  {
                    path: FAST_DECLINE_PATH,
                    element: (
                      <Provider store={store}>
                        <CYQRedZoneErrorModal />
                      </Provider>
                    ),
                  },
                ],
              },
              {
                path: QUOTES_PATH,
                element: <Quotes />,
              },
              {
                path: `${QUOTES_CONTINUE_PATH}/:policyLocator`,
                element: (
                  <Provider store={store}>
                    <QuotesContinueLoading />
                  </Provider>
                ),
              },
              {
                path: POLICIES_PATH,
                element: <Policies />,
                children: [
                  {
                    path: ":policyLocator",
                    element: (
                      <Provider store={store}>
                        <PolicyDetails />
                      </Provider>
                    ),
                    children: [
                      {
                        path: BASE_ENDORSEMENT_PATH,
                        element: <EndorsementLayout />,
                        children: [
                          {
                            path: "*",
                            element: (
                              <Navigate
                                to={EndorsementRoutes.INSURED_INFO}
                                replace
                              />
                            ),
                          },
                          {
                            path: EndorsementRoutes.INSURED_INFO,
                            element: <EndorsementInsuredInfoStep />,
                          },
                          {
                            path: EndorsementRoutes.COVERAGE,
                            element: <EndorsementCoverageStep />,
                          },
                          {
                            path: EndorsementRoutes.SUMMARY,
                            element: <EndorsementSummaryStep />,
                          },
                          {
                            path: EndorsementRoutes.UW_REVIEW,
                            element: <EndorsementUwReviewStep />,
                          },
                          {
                            path: EndorsementRoutes.CHECKOUT,
                            element: <EndorsementCheckoutStep />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: PENDING_CANCELLATION_PATH,
                element: <PendingCancellationPolicies />,
              },
              {
                path: RENEWALS_PATH,
                element: <Renewals />,
                children: [
                  { path: ":renewalLocator", element: <RenewalDetails /> },
                ],
              },
              {
                path: SEQUOIA_PATH,
                element: (
                  <Provider store={store}>
                    <Sequoia />
                  </Provider>
                ),
              },
              {
                path: `${SEQUOIA_CONTINUE_PATH}/:policyLocator`,
                element: (
                  <Provider store={store}>
                    <SequoiaContinueLoading />
                  </Provider>
                ),
              },
              {
                path: BULK_QUOTES_PATH,
                element: (
                  <ProtectedRoute
                    shouldBeLoggedIn={true}
                    allowedGroups={[
                      "ct_agent",
                      "ct_master_agent",
                      "agent",
                      "master_agent",
                      "binding_helping_agent",
                    ]}
                    whenNotAllowed={() => `/${OVERVIEW_PATH}`}>
                    <Provider store={store}>
                      <BulkQuotes />
                    </Provider>
                  </ProtectedRoute>
                ),
              },
              {
                path: SEQUOIA_UPLOAD_PATH,
                element: (
                  <ProtectedRoute
                    shouldBeLoggedIn={true}
                    allowedGroups={["super_admin"]}
                    whenNotAllowed={() => `/${OVERVIEW_PATH}`}>
                    <Provider store={store}>
                      <SequoiaUpload />
                    </Provider>
                  </ProtectedRoute>
                ),
              },
              {
                path: SEQUOIA_STATUSES_PATH,
                element: (
                  <ProtectedRoute
                    shouldBeLoggedIn={true}
                    allowedGroups={["super_admin"]}
                    whenNotAllowed={() => `/${OVERVIEW_PATH}`}>
                    <Provider store={store}>
                      <SequoiaStatuses />
                    </Provider>
                  </ProtectedRoute>
                ),
              },
              {
                path: BASE_NEW_POLICY_PATH,
                element: (
                  <ProtectedRoute
                    shouldBeLoggedIn={true}
                    allowedGroups={[
                      "ct_agent",
                      "ct_master_agent",
                      "agent",
                      "master_agent",
                      "binding_helping_agent",
                    ]}
                    whenNotAllowed={() => `/${OVERVIEW_PATH}`}>
                    <Provider store={store}>
                      <NewPolicyLayout />
                    </Provider>
                  </ProtectedRoute>
                ),
                children: newPolicyRoutes,
              },
            ],
          },
        ],
      },
    ],
  },
]);
